import logger from '@orn/shared-web/lib/logger';
import * as React from 'react';

import AssistantAddAccountFlow from './AssistantAddAccountFlow';

import type { IInvokeClientTool } from '@orn/global/assistant/wireProtocol.js';

interface IAssistantClientToolRunnerProps {
  /** The server's request to run a client tool in this browser. */
  invocation: IInvokeClientTool;
  /** Report the outcome back to the server (clears the pending invocation). */
  onResult: (toolId: string, result?: unknown, error?: string) => void;
}

interface IUnsupportedClientToolProps {
  invocation: IInvokeClientTool;
  onResult: (toolId: string, result?: unknown, error?: string) => void;
}

/** Report an error for a client tool this build doesn't know how to run, so the suspended turn isn't left hanging. */
const UnsupportedClientTool = ({ invocation, onResult }: IUnsupportedClientToolProps) => {
  const { toolId, toolName } = invocation;
  // Depend on the primitive fields, not the `invocation` object — a re-render
  // with a fresh-but-equal object reference must not re-fire the error report.
  React.useEffect(() => {
    logger.error('Assistant client tool not supported in this client', { toolName });
    onResult(toolId, undefined, `This client can't run "${toolName}".`);
  }, [toolId, toolName, onResult]);
  return null;
};

/**
 * Dispatcher for the assistant's *client-exposed action tools* — tools whose
 * handler runs in this browser. `AssistantPane` mounts it (keyed on
 * `invocation.toolId`) only while the server is waiting on a browser-side flow;
 * it routes each `toolName` to the component that runs it and reports the
 * result, then unmounts when the pane clears the pending invocation.
 *
 * Today the only client tool is `financial_account_connect`. A new one (e.g.
 * `verify_phone`) drops in as a sibling flow component plus one case here.
 */
const AssistantClientToolRunner = ({ invocation, onResult }: IAssistantClientToolRunnerProps) => {
  switch (invocation.toolName) {
    case 'financial_account_connect':
      return <AssistantAddAccountFlow toolId={invocation.toolId} onResult={onResult} />;
    default:
      return <UnsupportedClientTool invocation={invocation} onResult={onResult} />;
  }
};

export default AssistantClientToolRunner;
